import {
  NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID,
  USD_STABLE_ADDRESSES_BY_CHAIN_ID,
  WRAPPED_NATIVE_ADDRESSES,
} from '../constants/addresses';
import { CHAIN_IDS, SUPPORTED_CHAINS } from '../constants/chain';
import { TokenResult } from './0x/token-registry.types';
import { ChainAddress } from './models';
import { normalizeTokenAddress } from './normalizeAddress';
import { TokenInfo } from './token.types';

const POL_ADDRESS_MATIC = '0x0000000000000000000000000000000000001010';

export const isTokenAddressNativeAsset = (
  tokenAddress: string | undefined,
  chainId: number | undefined,
) => {
  if (!tokenAddress || !chainId) return undefined;

  const normalizedAddress = normalizeTokenAddress(tokenAddress, chainId);
  const nativeAddress = NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID[chainId];

  return (
    (nativeAddress != null &&
      normalizedAddress === normalizeTokenAddress(nativeAddress, chainId)) ||
    (chainId === CHAIN_IDS.MATIC &&
      normalizedAddress === normalizeTokenAddress(POL_ADDRESS_MATIC, chainId))
  );
};

export const isTokenInfoNativeAsset = (token: TokenInfo | undefined) =>
  isTokenAddressNativeAsset(token?.address, token?.chainId);

export const isTokenResultNativeAsset = (token: TokenResult | undefined) =>
  isTokenAddressNativeAsset(token?.address, token?.chainId);

export const doesTokenNeedSellApproval = (
  tokenAddress: string | undefined,
  chainId: number | undefined,
) => {
  if (!tokenAddress || !chainId) {
    return undefined;
  }
  // On Ethereum, the native token does not need approval
  return tokenAddress.toLowerCase() !== NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID[chainId].toLowerCase();
};

export const isTokenAddressWrappedNativeAsset = (
  tokenAddress: string | undefined,
  chainId: number | undefined,
): boolean | undefined => {
  if (!tokenAddress) return undefined;
  if (!chainId) return undefined;

  const wrappedNativeAddress = WRAPPED_NATIVE_ADDRESSES[chainId];

  return (
    wrappedNativeAddress != null &&
    normalizeTokenAddress(tokenAddress, chainId) ===
      normalizeTokenAddress(wrappedNativeAddress, chainId)
  );
};

/**
 * Whether the given address is a known USD-pegged stablecoin on the given
 * chain. Returns `false` for unsupported chains, missing inputs, or
 * unrecognized addresses. Source of truth:
 * `USD_STABLE_ADDRESSES_BY_CHAIN_ID` in `src/constants/addresses.ts`.
 */
export const isUsdStableAddress = (
  tokenAddress: string | undefined,
  chainId: number | undefined,
): boolean => {
  if (!tokenAddress || !chainId) return false;
  return (
    USD_STABLE_ADDRESSES_BY_CHAIN_ID[chainId]?.has(normalizeTokenAddress(tokenAddress, chainId)) ??
    false
  );
};

export const getNativeAddressFromSymbol = (
  symbol: string | undefined,
  chainId: number | undefined,
): ChainAddress | undefined => {
  if (!symbol || !chainId) {
    return undefined;
  }

  if (symbol === 'native') {
    return NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID[chainId];
  }

  const chain = SUPPORTED_CHAINS.find(
    ({ nativeCurrency, id }) =>
      nativeCurrency.symbol.toLowerCase() === symbol.toLowerCase() && chainId === id,
  );

  return chain != null ? NATIVE_TOKEN_ADDRESS_PER_CHAIN_ID[chain.id] : undefined;
};
